import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  clearStoredExternalPlanQueryParams,
  ExternalPlanQueryParams,
  hasExternalPlanQueryParams,
  readExternalPlanQueryParamsFromSearch,
  readStoredExternalPlanQueryParams,
  removeExternalPlanQueryParamsFromSearch,
  storeExternalPlanQueryParams,
} from '../utils/externalPlanDataStorage';

interface QueryParamsContextValue extends ExternalPlanQueryParams {
  clearQueryParams: () => void;
}

const QueryParamsContext = createContext<QueryParamsContextValue>({
  clearQueryParams: () => {},
});

export const useQueryParams = () => useContext(QueryParamsContext);

export const QueryParamsProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useState<ExternalPlanQueryParams>(() => readStoredExternalPlanQueryParams());

  const clearQueryParams = useCallback(() => {
    clearStoredExternalPlanQueryParams();
    setParams({});
  }, []);

  useEffect(() => {
    const filteredParams = readExternalPlanQueryParamsFromSearch(location.search);
    const cleanSearch = removeExternalPlanQueryParamsFromSearch(location.search);
    const currentLocation = `${location.pathname}${location.search}${location.hash}`;
    const cleanLocation = `${location.pathname}${cleanSearch}${location.hash}`;

    if (hasExternalPlanQueryParams(filteredParams)) {
      storeExternalPlanQueryParams(filteredParams);
      setParams(filteredParams);

      if (cleanLocation !== currentLocation) {
        navigate(cleanLocation, { replace: true });
      }

      return;
    }

    const storedParams = readStoredExternalPlanQueryParams();
    if (hasExternalPlanQueryParams(storedParams)) {
      setParams(storedParams);
      return;
    }

    setParams({});
    if (cleanLocation !== currentLocation) {
      navigate(cleanLocation, { replace: true });
    }
  }, [location.hash, location.search, location.pathname, navigate]);

  const value = useMemo<QueryParamsContextValue>(
    () => ({
      ...params,
      clearQueryParams,
    }),
    [clearQueryParams, params],
  );

  return <QueryParamsContext.Provider value={value}>{children}</QueryParamsContext.Provider>;
};
